import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert'
import { CheckCircle, ArrowLeft } from 'lucide-react';
import Background from '../../components/background'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)
    try {
      const resetUrl = `${window.location.origin}/#/auth/reset-password`

      console.log('Using direct reset URL:', resetUrl)

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetUrl,
      })

      if (error) throw error
      setIsSuccess(true)
      toast({
        title: 'Success',
        description: 'Password reset instructions have been sent to your email',
      })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const renderSuccessMessage = () => (
    <div className="flex flex-col w-full gap-6 items-center text-center">
      {/*<Alert>
        <AlertDescription>
          If your email exists in the system, you will receive a password reset link. The link will expire in 24 hours.
        </AlertDescription>
      </Alert>
      <Link to="/auth/login" className="w-full">
        <Button type="button" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
          Back to Login
        </Button>
      </Link>*/}
      <div className="mx-auto h-16 w-16 flex items-center justify-center">
        <CheckCircle className="h-16 w-16 rounded-full bg-green-200 p-3" />
      </div>

      <div className="space-y-1">
        <h1 className="text-2xl font-bold text-white">Check your email</h1>
        <p className="text-slate-400">
          We've sent password reset instructions to {email}
        </p>
      </div>

      <div className="space-y-4">
        <p className="text-sm text-slate-400 pb-5">
          Click the link in the email to reset your password.
        </p>
        <Link to="/auth/login">
         <Button className="w-full bg-white text-black group hover:text-white  font-light">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:invert"/> Back to Login
          </Button>
        </Link>
      </div>


    </div>
  )

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="flex flex-col w-full gap-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-medium text-white">Reset password</h1>
        <p className="text-slate-400 font-light">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>
      <div className="flex flex-col mt-2">
        <Label htmlFor="email" className="text-sm font-light text-white mb-2">
          Email
        </Label>
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
          className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20"
        />
      </div>
      {formError && (
        <Alert variant="destructive">
          <AlertDescription className="text-white">{formError}</AlertDescription>
        </Alert>
      )}
      <Button type="submit" className="w-full font-light bg-primary hover:bg-primary/90 text-primary-foreground" disabled={isLoading}>
        {isLoading ? 'Sending...' : 'Send Reset Email'}
      </Button>

      <div className="text-center">
        <Link to="/auth/login" className="text-sm text-slate-400 hover:text-primary flex items-center justify-center gap-1 transition-colors group hover:text-primary">
          Back to Login
        </Link>
      </div>
    </form>
  )

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}/>
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8"/>
              </div>
            </div>
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md">
                <h1 className="text-4xl font-bold mb-4 text-white">
                  Forgot Your Password?
                </h1>
                <p className="text-lg opacity-90 mb-8 text-white">
                  No worries! We'll send you reset instructions to get you back into your account.
                </p>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Panel - Login Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto"/>
              </div>
              {isSuccess ? renderSuccessMessage() : renderForm()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
